import { getProjectedDate } from "@utils/utils";
import { useState } from "react";

import { Button, Paragraph, SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  CreateGoalBankTargetsInput,
  ShortTermTimelineEstimationEnums,
  TargetTimelineEstimationEnums,
  useCreateGoalBankTargetsMutation,
} from "@api/graphql/types-and-hooks";
import { GoalBankGoalsFormConfig, Goals } from "@components/GoalsBank/constants";
import { useGetShortTermGoals } from "@components/GoalsBank/hooks/useGetShortTermGoals";
import { renderGoalBankFormDialog } from "@components/GoalsBank/utils";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { convertDBString, convertReadableString } from "@components/forms/utils";
import { notifySuccess } from "@components/notifications/notifications";

interface GoalBankTargetsAddNewFormProps {
  folderId: string;
  refetchGoals: () => void;
  formType: Goals;
  hasGoals: boolean;
}

export const GoalBankTargetsAddNewForm: React.FC<GoalBankTargetsAddNewFormProps> = ({
  folderId,
  refetchGoals,
  formType,
  hasGoals = false,
}) => {
  const [open, setOpen] = useState(false);
  const config = GoalBankGoalsFormConfig[formType];
  const createTargets = useCreateGoalBankTargetsMutation({});

  const { formState, watch, RegisteredFormInput, RegisteredFormTextArea, RegisteredFormSelected, reset, onSubmit } =
    useFormUtils<CreateGoalBankTargetsInput>({
      defaultValues: config.defaultValues,
      mutationFn: async (params) => {
        createTargets.mutateAsync(
          {
            input: {
              targetsData: params.targetsData.map((target) => ({
                ...target,
                timelineEstimationValue: target.timelineEstimationValue ? Number(target.timelineEstimationValue) : null,
                timelineEstimationType: target.timelineEstimationType
                  ? (convertDBString(target.timelineEstimationType) as TargetTimelineEstimationEnums)
                  : null,
              })),
              folderId: folderId || params.folderId,
            },
          },
          {
            onSuccess: async () => {
              await refetchGoals();
              notifySuccess(`Successfully Created ${config.title}`);
              setOpen(false);
              reset();
            },
            onError: async (error: unknown) => {
              console.error(`Error when saving ${config.title}`, error);
              setOpen(true);
            },
          },
        );
      },
    });

  const duration = watch("targetsData.0.timelineEstimationValue");
  const durationType = watch("targetsData.0.timelineEstimationType");

  const { shortTermGoals } = useGetShortTermGoals(folderId);

  const dialog = renderGoalBankFormDialog({
    title: config.title,
    openForm: open,
    setOpenForm: setOpen,
    onSubmit: async () => {
      await onSubmit();
    },
    children: (
      <FormContainer onSubmit={onSubmit}>
        <RegisteredFormInput required formKey="targetsData.0.targetName" formState={formState} label="Target Name" />
        <RegisteredFormSelected
          required={true}
          formKey="targetsData.0.goalBankId"
          formState={formState}
          items={
            (shortTermGoals?.getGoalBankGoals?.edges.map((edge) => {
              return {
                id: edge?.node?.id,
                primary: edge?.node?.goalName,
                className: "ph-no-capture",
              };
            }) as SelectItem[]) || []
          }
          title="Short Term Goal"
          placeholderText={"Select Short Term Goal"}
        />
        <RegisteredFormTextArea
          required={false}
          rows={4}
          formKey="targetsData.0.targetDescription"
          formState={formState}
          label="Description"
        />
        <RegisteredFormInput
          required={false}
          formKey="targetsData.0.masteryCriteria"
          formState={formState}
          label="Mastery Criteria"
        />
        <Paragraph displayType="loud" colorType="primary">
          Expected Mastery Date
        </Paragraph>
        <div className="w-full flex items-center justify-start mt-2 gap-x-4">
          <RegisteredFormSelected
            required={false}
            formKey="targetsData.0.timelineEstimationValue"
            formState={formState}
            items={
              Array.from({ length: 60 }, (_, index) => ({
                primary: `${index + 1}`,
              })) as unknown as SelectItem[]
            }
            title="Duration"
            placeholderText={"Select Duration"}
            className="w-full"
          />
          <RegisteredFormSelected
            required={false}
            formKey="targetsData.0.timelineEstimationType"
            formState={formState}
            items={
              Object.values(ShortTermTimelineEstimationEnums).map((o) => ({
                primary: convertReadableString(o),
              })) as SelectItem[]
            }
            title="Time Period"
            className="w-full"
            placeholderText={"Select Time Period"}
          />
        </div>
        {duration && durationType && (
          <div>
            Expected Mastery Date:{" "}
            {getProjectedDate(Number(duration), durationType.toLowerCase() as "days" | "weeks" | "months")}
          </div>
        )}
      </FormContainer>
    ),
  });

  return (
    <>
      {hasGoals ? (
        <div
          onClick={() => setOpen(true)}
          className="group cursor-pointer content-center items-center justify-center rounded-lg border border-dashed border-limestone-200 px-6 hover:border-rio-600"
        >
          <div className="flex items-center justify-center">
            <Button
              text="+ Add New"
              className="pl-1 text-rio-600 group-hover:text-rio-500"
              appearance="text"
              buttonAction="action"
              onClick={(e) => {
                if (e) {
                  e.stopPropagation();
                }
                setOpen(true);
              }}
            />
          </div>
          {dialog}
        </div>
      ) : (
        <div
          onClick={() => setOpen(true)}
          className="group cursor-pointer content-center items-center justify-center rounded-lg border border-dashed border-limestone-200 px-6 hover:border-rio-600"
        >
          <div className="flex items-center justify-center">
            <Button
              text="+ Add New"
              className="pl-1 text-rio-600 group-hover:text-rio-500"
              appearance="text"
              buttonAction="action"
              onClick={(e) => {
                if (e) e.stopPropagation();
                setOpen(true);
              }}
            />
          </div>
          {dialog}
        </div>
      )}
    </>
  );
};
